import {
  AlignmentType,
  BorderStyle,
  Document,
  ExternalHyperlink,
  ImageRun,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';

import type { Company } from '../@types/Company';
import { JWTHeader } from './JWTHeader';
import { DJ_URL } from './routes';

const fetchImageAsBase64 = async(imageUrl: string): Promise<string | null> => {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        resolve(base64.split(',')[1]);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const fetchQRCodeAsBase64 = async(url: string, size: number = 200): Promise<string | null> => {
  const encodedUrl = encodeURIComponent(url);
  const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodedUrl}`;
  return fetchImageAsBase64(qrApiUrl);
};

export const fetchPrivacyPolicyLink = async(
  siren: string,
  jwt: string,
): Promise<string | null> => {
  try {
    const response = await fetch(
      `/api/public/dj/companies/${siren}/privacy-policy`,
      JWTHeader(jwt),
    );
    if (response.ok) {
      const data = await response.json();
      if (data.link) {
        return `${DJ_URL}${data.link}`;
      }
    }
  } catch {
    // silently fail
  }
  return null;
};

/**
 * Strip "#" prefix from a hex color for use in docx (which expects bare hex).
 * Falls back to a default dark blue.
 */
const toDocxHex = (hex: string | undefined, fallback = '1A237E'): string => {
  if (!hex) {
    return fallback;
  }
  return hex.replace('#', '');
};

/**
 * Determine whether a hex colour is perceptually "light" using the YIQ formula.
 * Returns true when white text would be hard to read on that background.
 */
const isLightBackground = (hex: string): boolean => {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 186;
};

const BORDER_NONE = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' } as const;
const CELL_BORDERS_NONE = {
  top: BORDER_NONE,
  bottom: BORDER_NONE,
  left: BORDER_NONE,
  right: BORDER_NONE,
} as const;

function sectionHeading(text: string): Paragraph {
  return new Paragraph({
    spacing: { before: 240, after: 120 },
    shading: { type: ShadingType.CLEAR, fill: 'F2F2F2' },
    children: [new TextRun({ text, bold: true, size: 22, font: 'Arial' })],
  });
}

function bodyText(text: string, opts?: { bold?: boolean; italic?: boolean; size?: number }): Paragraph {
  return new Paragraph({
    alignment: AlignmentType.JUSTIFIED,
    spacing: { after: 80 },
    children: [new TextRun({
      text,
      size: opts?.size ?? 20,
      font: 'Arial',
      bold: opts?.bold,
      italics: opts?.italic,
    })],
  });
}

function bulletItem(labelText: string, valueText: string): Paragraph {
  return new Paragraph({
    spacing: { after: 60 },
    children: [
      new TextRun({ text: '•  ', size: 20, font: 'Arial' }),
      new TextRun({ text: labelText, bold: true, size: 20, font: 'Arial' }),
      new TextRun({ text: valueText, size: 20, font: 'Arial' }),
    ],
  });
}

function fieldLine(labelText: string, valueText: string = ''): Paragraph {
  return new Paragraph({
    spacing: { after: 60 },
    children: [
      new TextRun({ text: labelText, bold: true, size: 20, font: 'Arial' }),
      new TextRun({ text: valueText, size: 20, font: 'Arial' }),
    ],
  });
}

function emptyLine(after = 60): Paragraph {
  return new Paragraph({ children: [], spacing: { after } });
}

function buildCopyContent(
  mainTitle: string,
  copySubtitle: string,
  companyName: string,
  companySiret: string,
  companyAddress: string,
  bannerBgHex: string,
  bannerTextHex: string,
  privacyPolicyLink: string | null,
  qrCodeBase64: string | null,
  referentEmail: string | null = null,
): (Paragraph | Table)[] {
  const elements: (Paragraph | Table)[] = [];

  // ── Top coloured banner (full-width table cell for edge-to-edge fill) ──
  const mmToTwipsBanner = (mm: number) => Math.round(mm * 56.7);
  elements.push(
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: BORDER_NONE,
        bottom: BORDER_NONE,
        left: BORDER_NONE,
        right: BORDER_NONE,
        insideHorizontal: BORDER_NONE,
        insideVertical: BORDER_NONE,
      },
      rows: [
        new TableRow({
          height: { value: mmToTwipsBanner(12), rule: 'atLeast' },
          children: [
            new TableCell({
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: CELL_BORDERS_NONE,
              shading: { type: ShadingType.CLEAR, fill: bannerBgHex },
              verticalAlign: 'center',
              margins: {
                top: mmToTwipsBanner(3),
                bottom: mmToTwipsBanner(3),
                left: mmToTwipsBanner(4),
                right: mmToTwipsBanner(4),
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.LEFT,
                  spacing: { before: 0, after: 0 },
                  children: [new TextRun({
                    text: mainTitle,
                    bold: true,
                    size: 24,
                    font: 'Arial',
                    color: bannerTextHex,
                  })],
                }),
              ],
            }),
          ],
        }),
      ],
    }),
  );

  // ── Copy subtitle (e.g. "EXEMPLAIRE DE L'AGENCE") ────────────────────
  elements.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 160, after: 160 },
      children: [new TextRun({
        text: copySubtitle,
        bold: true,
        size: 22,
        font: 'Arial',
      })],
    }),
  );

  // ── Agency info ──────────────────────────────────────────────────────
  elements.push(fieldLine('Nom de l\'agence : ', companyName));

  // SIRET on same conceptual line
  elements.push(fieldLine('SIRET : ', companySiret));
  elements.push(fieldLine('Adresse de l\'agence : ', companyAddress));
  elements.push(emptyLine(80));

  // ── NOTRE MISSION & VOS DONNÉES PERSONNELLES ─────────────────────────
  elements.push(sectionHeading('NOTRE MISSION & VOS DONNÉES PERSONNELLES'));
  elements.push(bodyText(
    'Vous nous avez sollicités pour procéder à l\'évaluation de votre bien, ce dont nous vous remercions. '
    + 'Afin de vous fournir un avis de valeur précis, nous sommes amenés à prendre des photos voire des vidéos '
    + 'de l\'intérieur comme de l\'extérieur du bien.',
  ));
  elements.push(bodyText(
    'Ce document est destiné à recueillir votre consentement à la prise de photos et/ou de vidéos. '
    + 'Dans la mesure où ces photos et/ou vidéos seront annexées à un dossier comportant vos données '
    + 'd\'identification et de contact, celles-ci peuvent être considérées comme des données à caractère '
    + 'personnel. Nous vous expliquons notre politique de traitement des données personnelles.',
  ));

  // ── CADRE JURIDIQUE (RGPD) ──────────────────────────────────────────
  elements.push(sectionHeading('CADRE JURIDIQUE (RGPD)'));
  elements.push(bodyText(
    'Pour le cas où ces photos et/ou vidéos pourraient être considérées comme des données à caractère '
    + 'personnel vous concernant, le traitement de celles-ci aurait pour :',
  ));
  elements.push(bulletItem('Finalité : ', 'L\'établissement de l\'avis de valeur sollicité.'));
  elements.push(bulletItem('Base légale : ', 'Votre consentement explicite (art. 6.1. a), RGPD)'));
  elements.push(bulletItem('Durée de conservation : ', '6 mois à compter de la remise de l\'avis de valeur.'));
  elements.push(bulletItem(
    'Destinataires : ',
    'votre conseiller et le service interne de l\'agence et les partenaires techniques. '
    + 'Ces données ne seront, en aucun cas, communiquées à des tiers. '
    + 'Elles seront stockées de manière sécurisée sur des serveurs hébergés dans l\'Union européenne.',
  ));

  // ── VOS DROITS À TOUT MOMENT ────────────────────────────────────────
  elements.push(sectionHeading('VOS DROITS À TOUT MOMENT'));
  elements.push(bodyText(
    'Vous restez maître de vos données. Vous disposez d\'un droit d\'accès, de rectification, '
    + 'd\'effacement et de retrait de votre consentement.',
  ));
  elements.push(fieldLine('Contact mail dédié (email de contact RGPD de l\'agence) : ', referentEmail ?? ''));
  elements.push(emptyLine(40));

  // ── VOTRE CONSENTEMENT ──────────────────────────────────────────────
  elements.push(sectionHeading('VOTRE CONSENTEMENT'));
  elements.push(
    new Paragraph({
      alignment: AlignmentType.RIGHT,
      spacing: { after: 80 },
      children: [new TextRun({
        text: 'En qualité de   Propriétaire, occupant',
        italics: true,
        size: 20,
        font: 'Arial',
      })],
    }),
  );

  elements.push(fieldLine(
    'Je soussigné(e) : ',
    '________________________________________________________________________________',
  ));
  elements.push(fieldLine(
    'Demeurant : ',
    '____________________________________________________________________________________',
  ));
  elements.push(emptyLine(40));

  elements.push(bodyText('[  ]  autorise l\'Agence à prendre des photographies de mon domicile.'));
  elements.push(bodyText('[  ]  reconnais avoir été informé(e) de la collecte de mes données.'));
  elements.push(bodyText('[  ]  accepte le traitement de ces données pour cet avis de valeur.'));
  elements.push(emptyLine(40));

  // ── Précision importante ────────────────────────────────────────────
  elements.push(
    new Paragraph({
      spacing: { after: 80 },
      shading: { type: ShadingType.CLEAR, fill: 'FFF3CD' },
      children: [
        new TextRun({
          text: 'Précision importante : ',
          bold: true,
          size: 20,
          font: 'Arial',
        }),
        new TextRun({
          text: 'Cette autorisation est donnée sans engagement contractuel et ne constitue en aucun cas un mandat de vente.',
          size: 20,
          font: 'Arial',
        }),
      ],
    }),
  );

  // ── Date & Signature ────────────────────────────────────────────────
  elements.push(fieldLine('Fait en deux exemplaires à ________________  Le : ________________'));
  elements.push(emptyLine(40));
  elements.push(fieldLine('Signature / (Précédée de la mention "Lu et approuvé")'));
  elements.push(emptyLine(40));
  elements.push(emptyLine(40));

  // ── Privacy policy link + QR code ───────────────────────────────────
  elements.push(
    new Paragraph({
      border: { top: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } },
      spacing: { before: 100, after: 60 },
      children: [new TextRun({
        text: 'Lien de la politique de traitement RGPD de l\'agence :',
        bold: true,
        size: 20,
        font: 'Arial',
      })],
    }),
  );

  if (privacyPolicyLink) {
    // Use a 2-column table: link on the left, QR code on the right
    const linkParagraphs: Paragraph[] = [
      new Paragraph({
        spacing: { after: 40 },
        children: [
          new ExternalHyperlink({
            link: privacyPolicyLink,
            children: [new TextRun({
              text: privacyPolicyLink,
              size: 18,
              font: 'Arial',
              color: '0066CC',
              underline: {},
            })],
          }),
        ],
      }),
      new Paragraph({
        spacing: { after: 0 },
        children: [new TextRun({
          text: 'Scannez le QR code ci-contre pour y accéder directement →',
          italics: true,
          size: 16,
          font: 'Arial',
          color: '666666',
        })],
      }),
    ];

    if (qrCodeBase64) {
      elements.push(
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: BORDER_NONE,
            bottom: BORDER_NONE,
            left: BORDER_NONE,
            right: BORDER_NONE,
            insideHorizontal: BORDER_NONE,
            insideVertical: BORDER_NONE,
          },
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  width: { size: 70, type: WidthType.PERCENTAGE },
                  borders: CELL_BORDERS_NONE,
                  verticalAlign: 'center',
                  children: linkParagraphs,
                }),
                new TableCell({
                  width: { size: 30, type: WidthType.PERCENTAGE },
                  borders: CELL_BORDERS_NONE,
                  verticalAlign: 'center',
                  children: [
                    new Paragraph({
                      alignment: AlignmentType.CENTER,
                      children: [
                        new ImageRun({
                          data: Uint8Array.from(atob(qrCodeBase64), c => c.charCodeAt(0)),
                          transformation: { width: 90, height: 90 },
                          type: 'png',
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      );
    } else {
      elements.push(...linkParagraphs);
    }
  } else {
    elements.push(
      new Paragraph({
        spacing: { after: 60 },
        children: [new TextRun({
          text: '(lien non disponible)',
          italics: true,
          size: 18,
          font: 'Arial',
          color: '999999',
        })],
      }),
    );
  }

  return elements;
}

export const generateRgpdVisitDocx = async(
  company: Company | null,
  jwt: string | null,
  referentEmail: string | null = null,
): Promise<void> => {
  const companyName = company?.brandingSettings?.commercialName || company?.name || '';
  const companySiret = company?.siren ? `${company.siren}XXXXX` : '';
  const companyAddress = company?.details
    ? `${company.details.address || ''}, ${company.details.postalCode || ''} ${company.details.city || ''}`.trim()
    : '';

  const color1 = company?.brandingSettings?.color1;
  const bannerBgHex = toDocxHex(color1);
  const bannerTextHex = isLightBackground(bannerBgHex) ? '000000' : 'FFFFFF';

  let privacyPolicyLink: string | null = null;
  if (company?.siren && jwt) {
    privacyPolicyLink = await fetchPrivacyPolicyLink(company.siren, jwt);
  }

  const qrCodeBase64 = privacyPolicyLink
    ? await fetchQRCodeAsBase64(privacyPolicyLink)
    : null;

  const mainTitle = 'CONSENTEMENT A LA PRISE DE PHOTOS ET / OU DE VIDEOS DE VOTRE BIEN';

  const agencyCopy = buildCopyContent(
    mainTitle,
    'EXEMPLAIRE DE L\'AGENCE',
    companyName,
    companySiret,
    companyAddress,
    bannerBgHex,
    bannerTextHex,
    privacyPolicyLink,
    qrCodeBase64,
    referentEmail,
  );

  const ownerCopy = buildCopyContent(
    mainTitle,
    'EXEMPLAIRE DU PROPRIÉTAIRE DU BIEN IMMOBILIER',
    companyName,
    companySiret,
    companyAddress,
    bannerBgHex,
    bannerTextHex,
    privacyPolicyLink,
    qrCodeBase64,
    referentEmail,
  );

  const mmToTwips = (mm: number) => Math.round(mm * 56.7);

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: mmToTwips(6),
              right: mmToTwips(12),
              bottom: mmToTwips(10),
              left: mmToTwips(12),
            },
          },
        },
        children: agencyCopy,
      },
      {
        properties: {
          page: {
            margin: {
              top: mmToTwips(6),
              right: mmToTwips(12),
              bottom: mmToTwips(10),
              left: mmToTwips(12),
            },
          },
        },
        children: ownerCopy,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, 'Document_RGPD_Premiere_Visite.docx');
};
